import { makeVar } from "@apollo/client";

import type {
  RosterManagementLeagueFragment,
  RosterManagementNationalityFragment,
  RosterManagementPositionFragment,
} from "@/generated/graphql";

export const DEFAULT_MIN_AGE = 18;
export const DEFAULT_MAX_AGE = 30;

export const DEFAULT_MIN_COST = 10000;
export const DEFAULT_MAX_COST = 20000000;

export const DEFAULT_MIN_MONTHS_REMAINING = 0;
export const DEFAULT_MAX_MONTHS_REMAINING = 60;

export const DEFAULT_MIN_METRIC = 0;
export const DEFAULT_MAX_METRIC = 100;

export const MIN_AGE = 15;
export const MAX_AGE = 50;

export const MIN_COST = 0;
export const MAX_COST = 100000000;

export const MIN_MONTHS_REMAINING = 0;
export const MAX_MONTHS_REMAINING = 60;

export const MIN_METRIC = 0;
export const MAX_METRIC = 100;

/**
 *
 */
export interface IFilterOption {
  name: string;
  id: string;
}

/**
 *
 */
export interface IFilterValueRange {
  min: number;
  max: number;
}

/**
 *
 */
export interface IFilterValues {
  roles?: IFilterOption[] | null;
  leagues?: RosterManagementLeagueFragment[] | null;
  competitions?: IFilterOption[] | null;
  nationalities?: RosterManagementNationalityFragment[] | null;
  positions?: RosterManagementPositionFragment[] | null;
  clubs?: IFilterOption[] | null;
  age?: IFilterValueRange;
  salary?: IFilterValueRange;
  monthsRemaining?: IFilterValueRange;
  playerStyleFit?: IFilterValueRange;
  teamStyleFit?: IFilterValueRange;
  gpr?: IFilterValueRange;
  carrying?: IFilterValueRange;
  dribbling?: IFilterValueRange;
  passing?: IFilterValueRange;
  shooting?: IFilterValueRange;
  gkPassing?: IFilterValueRange;
  gkPositioning?: IFilterValueRange;
  gkShotStopping?: IFilterValueRange;

  // Dynamic custom metrics - using template literal type for organization-specific metrics
  [key: `custom_${string}`]: IFilterValueRange | undefined;
}

export const defaultFilterValues: IFilterValues = {
  age: { min: DEFAULT_MIN_AGE, max: DEFAULT_MAX_AGE },
  salary: { min: DEFAULT_MIN_COST, max: DEFAULT_MAX_COST },
  monthsRemaining: {
    min: DEFAULT_MIN_MONTHS_REMAINING,
    max: DEFAULT_MAX_MONTHS_REMAINING,
  },
  playerStyleFit: { min: DEFAULT_MIN_METRIC, max: DEFAULT_MAX_METRIC },
  teamStyleFit: { min: DEFAULT_MIN_METRIC, max: DEFAULT_MAX_METRIC },
  gpr: { min: DEFAULT_MIN_METRIC, max: DEFAULT_MAX_METRIC },
  carrying: { min: DEFAULT_MIN_METRIC, max: DEFAULT_MAX_METRIC },
  dribbling: { min: DEFAULT_MIN_METRIC, max: DEFAULT_MAX_METRIC },
  passing: { min: DEFAULT_MIN_METRIC, max: DEFAULT_MAX_METRIC },
  shooting: { min: DEFAULT_MIN_METRIC, max: DEFAULT_MAX_METRIC },
  gkPassing: { min: DEFAULT_MIN_METRIC, max: DEFAULT_MAX_METRIC },
  gkPositioning: { min: DEFAULT_MIN_METRIC, max: DEFAULT_MAX_METRIC },
  gkShotStopping: { min: DEFAULT_MIN_METRIC, max: DEFAULT_MAX_METRIC },
};

export const filterValuesVar = makeVar<IFilterValues | null>(null);
export const showFilterVar = makeVar<boolean>(false);
export const defaultFilterValuesVar =
  makeVar<IFilterValues>(defaultFilterValues);

/**
 * Isolated reactive variable for Shadow Team filter state.
 * Prevents Shadow Team filter selections from leaking into the
 * regular player search filters (SE-3827).
 */
export const shadowTeamFilterValuesVar = makeVar<IFilterValues | null>(null);

/**
 * Generate default filter values for organization custom metrics
 * @param metricConfigs - Array of organization metric configurations
 * @returns Partial filter values with defaults for custom metrics
 */
export const generateCustomMetricFilterDefaults = (
  metricConfigs: {
    backendKey: string;
    minValue: number;
    maxValue: number;
  }[]
): Partial<IFilterValues> => {
  return metricConfigs.reduce<Partial<IFilterValues>>((defaults, config) => {
    const filterKey = `custom_${config.backendKey}` as const;
    return {
      ...defaults,
      [filterKey]: {
        min: config.minValue,
        max: config.maxValue,
      },
    };
  }, {});
};
